import { useQueries } from '@tanstack/react-query';

import { useConfigContext } from '../context';
import { getLanguage, TLanguageData } from '../api';

export const LANGUAGE_DATA_QUERY_KEY = 'languageData';
const queryFunction = async (
  baseUrl: string,
  bundle: string,
  locale: string,
): Promise<TLanguageData> => {
  const result = await getLanguage(baseUrl, bundle, locale);

  return {
    ...result,
    keys: {
      ...result.keys,
    },
  };
};

export const useLanguages = ({
  enabled,
  locale,
  bundles,
}: {
  enabled: boolean;
  locale?: string | null;
  bundles: string[];
}) => {
  const { config } = useConfigContext();
  if (!config?.apiUrls?.aleweb) {
    throw new Error('Missing API URL for aleweb');
  }

  if (!bundles?.length) {
    throw new Error('Missing bundles');
  }

  const languageQueries = useQueries({
    queries: bundles.map((bundle) => {
      return {
        queryKey: [LANGUAGE_DATA_QUERY_KEY, locale, bundle],
        queryFn: () =>
          queryFunction(
            config?.apiUrls?.aleweb as string,
            bundle,
            locale as string,
          ),
        retry: false,
        staleTime: Infinity,
        enabled,
      };
    }),
  });

  const { isFetching, isLoading, isFetched, keys, isError, error } =
    languageQueries.reduce(
      (acc, query) => {
        if (query.isError) {
          return {
            ...acc,
            isError: true,
            error: query.error,
          };
        }

        return {
          ...acc,
          isFetching: acc.isFetching || query.isFetching,
          isLoading: acc.isLoading || query.isLoading,
          isFetched: acc.isFetched || query.isFetched,
          keys: {
            ...acc.keys,
            ...(query.isSuccess && query.data ? query.data.keys : {}),
          },
        };
      },
      {
        isFetching: false,
        isLoading: false,
        isFetched: false,
        keys: {} as Record<string, string>,
        isError: false,
        error: null as Error | null,
      },
    );

  return {
    queries: languageQueries,
    isFetched,
    isFetching,
    isLoading,
    keys,
    isError,
    error,
  };
};
